import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ProductCard from "./ProductCard";
import { useNavigate } from "react-router-dom";

const AllProduct = ({ heading, initialCategory }) => {
  const navigate = useNavigate();
  const productData = useSelector((state) => state.product.productList);
  const categoriesFromRedux = useSelector((state) => state.product.categoryList);

  // Use dynamic categories if available, else fallback to deriving from products (failsafe)
  const sortedCategoryList = categoriesFromRedux?.length > 0 
    ? categoriesFromRedux 
    : [...new Set(productData.map((el) => el.category))].map(c => ({ value: c, name: c }));

  // We need an array of string values for the product grid rendering logic below to work the same
  const categoryValues = sortedCategoryList.map(c => c.value || c);

  // ✅ Move helper function ABOVE useState
  const capitalize = (str) =>
    str
      ? str
          .split(/[\s_-]+/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join("")
      : "";

  // Default filter from URL or wait for categories to load
  const [filterBy, setFilterBy] = useState(initialCategory || "");
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (initialCategory) {
      setFilterBy(initialCategory);
    } else if (!filterBy && sortedCategoryList.length > 0) {
      // Default to the first category's value
      const firstCat = sortedCategoryList[0];
      setFilterBy(firstCat.value || firstCat);
    }
  }, [initialCategory, sortedCategoryList, filterBy]);

  useEffect(() => {
    if (!filterBy) return; // Wait until filter is set

    if (filterBy.toLowerCase() === "all") {
      setFilteredData(productData);
    } else {
      setFilteredData(
        productData.filter(
          (el) => el.category.toLowerCase() === filterBy.toLowerCase()
        )
      );
    }
  }, [filterBy, productData]);

  const handleFilterProduct = (category) => {
    setFilterBy(category);
    const lowerCategory = category.toLowerCase();
    navigate(lowerCategory === "all" ? "/menu" : `/menu/${lowerCategory}`);
  };

  const loadingArrayFeature = new Array(10).fill(null);

  return (
    <div className="relative z-10 flex flex-col items-center px-4 md:px-10">
      {/* Heading Section */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-3">
          Our Menu
        </h1>
        <div className="w-20 h-[3px] bg-[#FFB4AB] mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">
          100% eggless delights crafted with premium ingredients
        </p>
      </div>

      {/* Category Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mb-10">
        {/* <button
          onClick={() => handleFilterProduct("All")}
          className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 shadow-sm ${
            filterBy === "All"
              ? "bg-[#FFB4AB] text-gray-700"
              : "bg-white text-gray-700 hover:bg-[#FF9E95]"
          }`}
        >
          All Items
        </button> */}
        {sortedCategoryList.map((category) => (
          <button
            key={category.value || category}
            onClick={() => handleFilterProduct(category.value || category)}
            className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 shadow-sm ${
              (category.value || category).toLowerCase() === filterBy.toLowerCase()
                ? "bg-[#FFB4AB] text-gray-700"
                : "bg-white text-gray-700 hover:bg-[#FF9E95]"
            }`}
          >
            {category.name ? category.name : capitalize(category)}
          </button>
        ))}
      </div>

      {/* Product Grid */}
      <div className="w-full max-w-6xl">
        {categoryValues.length > 0 ? (
          filterBy === "All" ? (
            categoryValues.map((categoryVal) => {
              const productsInCategory = productData.filter(
                (p) => p.category === categoryVal
              );
              // Find the pretty name
              const catObj = sortedCategoryList.find(c => c.value === categoryVal);
              const displayName = catObj && catObj.name ? catObj.name : capitalize(categoryVal);
              return (
                <div key={categoryVal} className="mb-12">
                  <h3 className="text-2xl font-semibold text-[#5C3A21] mb-6 text-center md:text-left">
                    {displayName}
                  </h3>
                  <div className="flex flex-wrap justify-center gap-8">
                    {productsInCategory.map((product) => (
                      <ProductCard key={product._id} product={product} />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-wrap justify-center gap-8">
              {!filterBy ? (
                loadingArrayFeature.map((_, index) => (
                  <div
                    key={index + "loading"}
                    className="w-60 h-72 bg-[#F5E8D0] rounded-lg animate-pulse"
                  />
                ))
              ) : filteredData.length > 0 ? (
                filteredData.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))
              ) : (
                <p className="text-center text-gray-500 my-8 w-full text-lg">No items found in this category.</p>
              )}
            </div>
          )
        ) : (
          <p className="text-center text-[#8B5E3C]">No products available.</p>
        )}
      </div>
    </div>
  );
};

export default AllProduct;
